<template>
  <FormGroup>
    <FormField
      label="Building name"
      hint="Left blank, the building goes by its street address"
      :error="form.fieldErrors('name')"
    >
      <Input v-model="form.fields.name" :placeholder="building.location.address" maxlength="120" />
    </FormField>

    <FormField label="Year built" :error="form.fieldErrors('yearBuilt')">
      <Input v-model="form.fields.yearBuilt" type="number" min="0" placeholder="1928" />
    </FormField>

    <FormField
      label="Getting in"
      hint="Office hours, buzzer, visitor parking; Tallo uses this in tour confirmations"
      :error="form.fieldErrors('accessNotes')"
    >
      <Textarea
        v-model="form.fields.accessNotes"
        rows="3"
        maxlength="4000"
        placeholder="Leasing office on the ground floor, buzz 100. Visitor parking behind the building."
      />
    </FormField>
  </FormGroup>
</template>

<script setup lang="ts">
import { helpers, integer, maxLength, minValue } from '@vuelidate/validators';

import { FormField, FormGroup, Input, Textarea, useForm } from '@tallo/design-system';
import { BuildingDto, useUpdateBuilding } from '@tallo/investor/property';

/** The building's own fields; its address, sections and photos each have a sheet of their own */
const props = defineProps<{ building: BuildingDto }>();

const { mutateAsync: updateBuilding } = useUpdateBuilding();

const form = useForm<{ name: string; yearBuilt: number | string | null; accessNotes: string }>(
  { name: props.building.name, yearBuilt: props.building.yearBuilt, accessNotes: props.building.accessNotes ?? '' },
  {
    name: { maxLength: helpers.withMessage('Keep the name under 120 characters', maxLength(120)) },
    yearBuilt: {
      integer: helpers.withMessage('A whole year, like 1928', integer),
      minValue: helpers.withMessage('A whole year, like 1928', minValue(0)),
    },
    accessNotes: { maxLength: helpers.withMessage('Keep it under 4000 characters', maxLength(4000)) },
  },
);

/** An empty number input comes back as '' */
function yearOf(value: number | string | null): number | null {
  return value === '' || value === null ? null : Number(value);
}

function save() {
  return form.submit((fields) =>
    updateBuilding({
      buildingId: props.building.id,
      // A cleared name falls back to the street address on the server, as it does when the building is created
      changes: {
        name: fields.name.trim(),
        yearBuilt: yearOf(fields.yearBuilt),
        accessNotes: fields.accessNotes.trim() || null,
      },
    }),
  );
}

defineExpose({ save, form });
</script>
